import React, { useEffect, useMemo, useState } from "react";
import { collection, getFirestore, onSnapshot } from "firebase/firestore";
import { useEventId } from "../EventContext";
import {
  completedLoopsOf,
  latestLoopOf,
  loopClockOf,
  rankLoopResults,
  rankRelayResults,
} from "../utils/loopDisplay";
import {
  resolveInitialCategory,
  writeLastCategory,
} from "../utils/categoryPreference";
import { toMillis } from "../utils/firestoreTime";
import { displayElapsed } from "../utils/finishTime";

const API = import.meta.env.VITE_API_BASE_URL;

/**
 * Elapsed race time for a result.
 *
 * `finishTime` means two different things depending on how the event was timed,
 * which is why this cannot simply read one field:
 *
 *   - MYLAPS events store an elapsed duration there ("01:17:33.918").
 *   - Manually timed events store the clock instant of the finish (a Firestore
 *     Timestamp) and put the duration in `finishDuration`.
 *
 * Reading `finishTime` first therefore printed a raw
 * "Timestamp(seconds=…, nanoseconds=…)" across the venue board for every
 * manually timed race. `finishDuration` is checked first because it is
 * unambiguous, and any candidate that is not an HH:MM:SS string is rejected
 * rather than stringified.
 */
const durationText = (result = {}, category = null) => {
  const candidates = [result.finishDuration, result.finishTime, result.gunTime];
  const elapsed = candidates.find(
    (value) => typeof value === "string" && /^\d{1,3}:\d{2}:\d{2}/.test(value.trim())
  );
  if (elapsed) return displayElapsed(elapsed, category);

  // Nothing usable was stored, so derive it the way the results API does:
  // last lap crossing minus the category start.
  //
  // This board reads Firestore directly, and finishDuration is NOT a stored
  // field — the backend computes it per request. Manually timed events
  // therefore arrive here with only a Timestamp in finishTime, which is why
  // every row showed a dash once the raw Timestamp stopped being printed.
  const last = latestLoopOf(result);
  const start = toMillis(category?.startTime);
  if (!last || !Number.isFinite(start)) return "-";
  const span = last.timeMs - start;
  return span >= 0 ? formatSpan(span) : "-";
};

const categoryClock = (category, now, finisherCount = 0) => {
  // A buzzer-timed category has no mass start to count from — each athlete's
  // clock starts on their own press — so a running race clock would be
  // meaningless here. Report how many have posted a time instead.
  if (category?.isBuzzerTimed) {
    return finisherCount === 1 ? "1 TIME POSTED" : `${finisherCount} TIMES POSTED`;
  }
  const start = toMillis(category?.startTime);
  const cutoff = toMillis(category?.cutOffTime);
  if (!Number.isFinite(start)) return "WAITING FOR START";
  if (now < start) return `STARTS IN ${formatSpan(start - now)}`;
  if (Number.isFinite(cutoff) && now > cutoff) return "CATEGORY CLOSED";
  return `RACE TIME ${formatSpan(now - start)}`;
};

const formatSpan = (milliseconds) => {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const remainder = String(seconds % 60).padStart(2, "0");
  return `${hours}:${minutes}:${remainder}`;
};

export const VenueDisplay = () => {
  const { realEventId: eventId } = useEventId();
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  // Track the category by id, not by position: a roster edit can reorder the
  // category list, and an index would then silently point at a different race.
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!eventId) return;
    fetch(`${API}/api/timing/event_timing`)
      .then((response) => response.json())
      .then((events) =>
        setEvent(
          (events || []).find((item) => item.eventId === eventId) || null,
        ),
      );
  }, [eventId]);

  useEffect(() => {
    if (!eventId) return;
    return onSnapshot(
      collection(getFirestore(), "event_timing", eventId, "results"),
      (snapshot) => {
        setResults(
          snapshot.docs.map((document) => ({
            id: document.id,
            ...document.data(),
          })),
        );
      },
    );
  }, [eventId]);

  // Loop categories, plus any category that has buzzer-recorded times.
  //
  // The board was loop-only, so a ninja/obstacle category — which has no laps
  // at all — could never appear on it. Non-loop categories are admitted only
  // once a duration exists for them, so an ordinary trail event does not
  // suddenly grow tabs for categories this screen was never meant to show.
  const categories = useMemo(() => {
    const timed = new Set(
      results.filter((result) => result.finishDuration).map((result) => result.categoryId),
    );
    return (event?.categories || []).filter(
      (category) => category.isLooping || category.isBuzzerTimed || timed.has(category.id),
    );
  }, [event, results]);
  const category = useMemo(
    () =>
      categories.find((item) => item.id === selectedCategoryId) ||
      categories[0] ||
      null,
    [categories, selectedCategoryId],
  );

  // Settle on a category once the event loads: the one last chosen on this
  // screen, otherwise the first. The board used to rotate through every
  // category on a timer, which made it useless for watching a single race —
  // whatever you wanted to read scrolled away a few seconds later.
  useEffect(() => {
    if (!categories.length || selectedCategoryId) return;
    setSelectedCategoryId(resolveInitialCategory(eventId, categories));
  }, [categories, eventId, selectedCategoryId]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    writeLastCategory(eventId, categoryId);
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Ninja/obstacle and speed climbing: no laps, the elapsed time is the result.
  const isBuzzerTimed = !!category?.isBuzzerTimed;

  // Column template follows the columns actually rendered. The lap column is
  // hidden for buzzer races, and leaving its track in the grid left a dead
  // gap that pushed the time far to the right of its heading.
  const rowCols = isBuzzerTimed
    ? "md:grid-cols-[0.35fr_2.6fr_0.8fr_1fr]"
    : "md:grid-cols-[0.35fr_2.6fr_0.8fr_0.8fr_1fr]";

  const categoryResults = useMemo(
    () => results.filter((result) => result.categoryId === category?.id),
    [results, category?.id],
  );
  const leaderboard = useMemo(() => {
    // A buzzer-timed category is ranked on elapsed time alone: fastest first,
    // and rows without a time have not run yet, so they hold no place.
    if (category?.isBuzzerTimed) {
      return categoryResults
        .filter((result) => result.finishDuration)
        .slice()
        .sort((a, b) =>
          String(a.finishDuration).localeCompare(String(b.finishDuration)),
        );
    }
    return category?.isRelay
      ? rankRelayResults(categoryResults)
      : rankLoopResults(categoryResults);
  }, [categoryResults, category?.isRelay, category?.isBuzzerTimed]);
  const displayResults = useMemo(
    () =>
      (event?.categories || []).flatMap((item) => {
        const source = results.filter(
          (result) => result.categoryId === item.id,
        );
        return item.isLooping && item.isRelay
          ? rankRelayResults(source)
          : source;
      }),
    [event?.categories, results],
  );
  // Latest activity across all categories.
  //
  // Lap-timed rows are ordered by their most recent crossing. Buzzer-timed rows
  // have no crossings at all — no mats, no checkpoints — so latestLoopOf drops
  // them and a ninja result never reached this panel. They are ordered by when
  // the operator recorded them instead, which is the only "when" they carry.
  const recent = useMemo(() => {
    const entries = displayResults
      .map((result) => {
        const latest = latestLoopOf(result);
        if (latest) return { result, at: latest.timeMs, loop: latest.number };
        if (!result.finishDuration) return null;
        const at = toMillis(result.updatedAt);
        return { result, at: Number.isFinite(at) ? at : 0, loop: null };
      })
      .filter(Boolean);
    return entries.sort((a, b) => b.at - a.at).slice(0, 7);
  }, [displayResults]);

  return (
    // Viewport-relative type (vw) is what makes this board fill a venue screen,
    // but on a phone 1.45vw is under 6px — unreadable. Every vw size below is
    // therefore paired with a fixed px size that applies up to `md`, and the
    // two-column layout collapses to a single scrollable column. The desktop
    // rendering is unchanged: the vw values only take effect from `md` up.
    <main className="min-h-screen md:h-screen md:overflow-hidden bg-[#07111f] text-white flex flex-col font-sans">
      <header className="px-4 py-3 md:h-[12vh] md:px-[3vw] md:py-0 flex items-center justify-between border-b border-white/10 bg-[#0b1728]">
        <div className="min-w-0">
          <div className="text-[10px] md:text-[1.15vw] leading-[1.15] text-teal-300 font-bold">
            VIONIXASIA TIMING SYSTEM
          </div>
          <h1 className="text-lg md:text-[2.4vw] leading-tight font-black uppercase truncate">
            {event?.eventName || "Indonesia Ultra 24"}
          </h1>
        </div>
        <div className="text-right shrink-0 pl-3">
          <div className="text-xl md:text-[2.6vw] leading-[1.15] font-black tabular-nums">
            {new Date(now).toLocaleTimeString("en-GB", { hour12: false })}
          </div>
        </div>
      </header>

      <div className="px-4 py-2.5 md:h-[10vh] md:px-[3vw] md:py-0 flex items-center justify-between gap-3 bg-teal-500 text-[#07111f]">
        <div className="flex items-center gap-[1vw] min-w-0">
          <div className="text-xl md:text-[2.7vw] leading-[1.15] font-black uppercase truncate">
            {category?.name || "LOOP LEADERBOARD"}
          </div>
        </div>
        <div className="text-sm md:text-[2vw] leading-[1.15] font-black tabular-nums text-right shrink-0">
          {categoryClock(category, now, leaderboard.length)}
        </div>
      </div>

      {/* The gap between the two cards is a plain Tailwind step, not a vw value.
          A viewport-relative gap made the spacing impossible to reason about —
          you had to know the screen width to know the distance — and 8px reads
          the same on a laptop as on a venue screen, so scaling it bought
          nothing. Type sizes below stay in vw, where scaling genuinely matters. */}
      <section className="flex-1 min-h-0 flex flex-col gap-4 p-3 md:grid md:grid-cols-[2.15fr_1fr] md:p-[1.4vw]">
        <div className="min-h-0 rounded-xl md:rounded-[1.2vw] border border-white/10 bg-[#0b1728] overflow-hidden flex flex-col">
          {/* BIB and race time are the first things to go when width is scarce:
              the rank, athlete and loop count carry the story on a phone. */}
          <div className={`shrink-0 grid grid-cols-[2rem_1fr_3rem] ${rowCols} gap-2 md:gap-0 px-3 py-2 md:px-[1.3vw] md:py-[0.7vw] bg-white/5 text-[10px] md:text-[0.95vw] leading-[1.15] uppercase text-slate-400 font-bold`}>
            <span>Rank</span>
            <span>Athlete</span>
            <span className="hidden md:block text-center">Bib</span>
            {/* A buzzer-timed race has no laps; the time is the whole result. */}
            {!isBuzzerTimed && <span className="text-center md:text-center">Loops</span>}
            <span className={isBuzzerTimed ? "text-right" : "hidden md:block text-right"}>Time</span>
          </div>
          {/* The whole field, scrollable, rather than the top ten. Once the
              leaders finish, the rows that matter to an operator are the ones
              below them — who is about to come in — and a fixed top-ten cut
              hides exactly those. The header stays put while the list moves. */}
          <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-white/10">
            {leaderboard.map((runner, index) => (
              <div
                key={runner.id || runner.bib}
                className={`shrink-0 grid grid-cols-[2rem_1fr_3rem] ${rowCols} gap-2 md:gap-0 items-center px-3 py-2.5 md:px-[1.3vw] md:py-0 md:h-[6.2vh] ${index < 3 ? "bg-teal-400/[0.07]" : ""}`}
              >
                <span
                  className={`text-base md:text-[1.45vw] leading-[1.15] font-black ${index < 3 ? "text-teal-300" : "text-slate-400"}`}
                >
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm md:text-[1.45vw] leading-[1.15] font-extrabold uppercase truncate md:pr-[1vw]">
                    {runner.name || "-"}
                  </span>
                  {/* Fold the two hidden columns under the name on a phone. */}
                  <span className="md:hidden block text-[10px] text-slate-400 tabular-nums truncate">
                    BIB {runner.bib || "-"} ·{" "}
                    {durationText(runner, category)}
                  </span>
                </span>
                <span className="hidden md:block text-center text-[1.15vw] text-slate-300 tabular-nums">
                  {runner.bib || "-"}
                </span>
                {/* The lap count is meaningless in a buzzer-timed race, so the
                    time takes over that slot and becomes the headline figure. */}
                {!isBuzzerTimed && (
                  <span className="text-center text-xl md:text-[2vw] leading-[1.15] font-black text-teal-300 tabular-nums">
                    {completedLoopsOf(runner)}
                  </span>
                )}
                <span
                  className={
                    isBuzzerTimed
                      ? "text-right text-xl md:text-[2vw] leading-[1.15] font-black text-teal-300 tabular-nums"
                      : "hidden md:block text-right text-[1.25vw] font-bold tabular-nums"
                  }
                >
                  {durationText(runner, category)}
                </span>
              </div>
            ))}
            {!leaderboard.length && (
              <div className="py-12 md:py-0 md:h-[52vh] flex items-center justify-center text-sm md:text-[1.5vw] leading-[1.15] text-slate-500">
                WAITING FOR FIRST PASSING
              </div>
            )}
          </div>
        </div>

        <aside className="min-h-0 rounded-xl md:rounded-[1.2vw] border border-white/10 bg-[#0b1728] overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 md:px-[1.2vw] md:py-[1vw] bg-white/5">
            <h2 className="text-sm md:text-[1.35vw] leading-[1.15] font-black uppercase">
              Latest crossings
            </h2>
            <p className="text-[10px] md:text-[0.9vw] leading-[1.15] text-slate-400">
              Across all categories
            </p>
          </div>
          <div className="flex-1 divide-y divide-white/10">
            {recent.map(({ result: runner, at, loop }, index) => (
              <div
                key={`${runner.bib}-${at}`}
                className={`${index === 0 ? "bg-teal-400/[0.12]" : ""} px-3 py-2.5 md:px-[1.2vw] md:py-0 md:h-[8.15vh] flex items-center justify-between gap-3 md:gap-[1vw]`}
              >
                <div className="min-w-0">
                  <div className="text-xs md:text-[1.05vw] leading-[1.15] font-extrabold uppercase truncate">
                    {runner.name}
                  </div>
                  <div className="text-[10px] md:text-[0.82vw] leading-[1.15] text-slate-400 truncate">
                    BIB {runner.bib} · {runner.categoryId}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {/* A lap-timed row leads with its loop number; a buzzer row
                      has no laps, so its finish time is the headline. */}
                  <div className="text-sm md:text-[1.45vw] leading-[1.15] font-black text-teal-300 tabular-nums">
                    {loop != null ? `LOOP ${loop}` : durationText(runner, null)}
                  </div>
                  <div className="text-[10px] md:text-[0.9vw] leading-[1.15] text-slate-400 tabular-nums">
                    {loop != null
                      ? loopClockOf(runner)
                      : at
                        ? new Date(at).toLocaleTimeString("en-GB", { hour12: false })
                        : "--:--:--"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>

      {/* Sticky on a phone so the category switcher stays reachable while the
          page scrolls; a venue screen never scrolls, so it stays put there. */}
      <footer className="sticky bottom-0 md:static md:h-[6vh] border-t border-white/10 bg-[#0b1728] px-3 py-2 md:px-[3vw] md:py-0 flex items-center gap-2 md:gap-[0.6vw] overflow-x-auto">
        {categories.map((item) => (
          <button
            key={item.id}
            onClick={() => handleCategorySelect(item.id)}
            className={`px-3 py-1.5 md:px-[1vw] md:py-[0.45vw] rounded-full text-[11px] md:text-[0.85vw] leading-[1.15] font-bold whitespace-nowrap shrink-0 ${category?.id === item.id ? "bg-teal-400 text-[#07111f]" : "bg-white/5 text-slate-400"}`}
          >
            {item.name}
          </button>
        ))}
      </footer>
    </main>
  );
};

export default VenueDisplay;
